import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Table, Input, Typography, Tooltip } from "antd";
import "./RoiEffortsGrid.css";

const { Text } = Typography;

// Separate component for effort input to prevent re-rendering
const EffortInput = React.memo(
  ({ value, recordKey, onChange, onFocus, onBlur }) => {
    const inputRef = useRef(null);

    const handleChange = useCallback(
      (e) => {
        onChange(recordKey, e.target.value);
      },
      [recordKey, onChange]
    );

    const handleFocus = useCallback(
      (e) => {
        e.target.select();
        onFocus && onFocus(recordKey);
      },
      [recordKey, onFocus]
    );

    const handleBlur = useCallback(() => {
      onBlur && onBlur();
    }, [onBlur]);

    return (
      <Input
        ref={inputRef}
        type="number"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="roi-efforts-input"
        size="small"
        min={0}
        step={0.1}
        autoComplete="off"
      />
    );
  }
);

EffortInput.displayName = "EffortInput";

const RoiEffortsGrid = ({
  currency,
  costPerHour,
  onCalculationChange,
  defaultEfforts,
  dynamicGridData,
}) => {
  const [gridData, setGridData] = useState(() => {
    // Lazy initialization - only runs once on mount
    let dataSourceData = [];
    // First priority: Use dynamic grid data if available
    if (
      dynamicGridData &&
      dynamicGridData.dataSource &&
      Array.isArray(dynamicGridData.dataSource)
    ) {
      dataSourceData = dynamicGridData.dataSource.map((item, index) => ({
        key: index + 1,
        action_type: item["Action Type"] || "Internal Actions",
        info_text: item["info_text"] || "",
        times_taken: parseInt(item["Number of Actions"]) || 0,
        efforts_required: 1, // Default meaningful value for calculation
      }));
    }

    if (
      defaultEfforts &&
      typeof defaultEfforts === "object" &&
      !Array.isArray(defaultEfforts)
    ) {
      dataSourceData = dataSourceData.map((item) => {
        const effortValue = defaultEfforts[item.action_type];
        return effortValue !== undefined
          ? { ...item, efforts_required: effortValue }
          : item;
      });
    }

    return dataSourceData.length > 0 ? dataSourceData : [];
  });

  const [totalValues, setTotalValues] = useState({
    total_times: 0,
    total_time_taken: 0,
  });

  // Use refs to track focus state and prevent losing focus
  const focusedInputRef = useRef(null);
  const isUpdatingRef = useRef(false);

  // Reset grid data when defaultEfforts changes (when modal is reopened)
  useEffect(() => {
    if (
      !dynamicGridData ||
      !dynamicGridData.dataSource ||
      !Array.isArray(dynamicGridData.dataSource)
    ) {
      return;
    }

    let dataSourceData = dynamicGridData.dataSource.map((item, index) => ({
      key: index + 1,
      action_type: item["Action Type"] || "Internal Actions",
      info_text: item["info_text"] || "",
      times_taken: parseInt(item["Number of Actions"]) || 0,
      efforts_required: 1, // Default meaningful value for calculation
    }));

    // Apply saved efforts from defaultEfforts
    if (
      defaultEfforts &&
      typeof defaultEfforts === "object" &&
      !Array.isArray(defaultEfforts)
    ) {
      dataSourceData = dataSourceData.map((item) => {
        const effortValue = defaultEfforts[item.action_type];
        return effortValue !== undefined
          ? { ...item, efforts_required: effortValue }
          : item;
      });
    }

    setGridData(dataSourceData);
  }, [defaultEfforts, dynamicGridData]); // Dependencies include defaultEfforts for reset

  // Calculate totals whenever grid data changes
  useEffect(() => {
    const total_times = gridData.reduce(
      (sum, item) => sum + item.times_taken,
      0
    );
    const total_time_taken = gridData.reduce(
      (sum, item) => sum + item.times_taken * item.efforts_required,
      0
    );

    setTotalValues({
      total_times,
      total_time_taken,
    });

    // Calculate cost saving and notify parent component
    const costSaving = (
      (total_time_taken / 60) *
      parseFloat(costPerHour || 0)
    ).toFixed(2);

    // Pass cost saving, total time, and grid data to parent
    onCalculationChange &&
      onCalculationChange(costSaving, total_time_taken, gridData);
  }, [gridData, costPerHour, onCalculationChange]);

  const handleEffortsChange = useCallback((key, value) => {
    isUpdatingRef.current = true;

    const numericValue = parseFloat(value) >= 0 ? parseFloat(value) : 0;
    setGridData((prevData) =>
      prevData.map((item) =>
        item.key === key ? { ...item, efforts_required: numericValue } : item
      )
    );

    // Reset the updating flag after a short delay
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 100);
  }, []);

  const handleInputFocus = useCallback((key) => {
    focusedInputRef.current = key;
  }, []);

  const handleInputBlur = useCallback(() => {
    focusedInputRef.current = null;
  }, []);

  // Memoize the input render function to prevent recreating inputs
  const renderEffortsInput = useCallback(
    (value, record) => {
      if (record.key === "total") {
        return <Text className="roi-total-empty"></Text>;
      }

      return (
        <EffortInput
          value={value}
          recordKey={record.key}
          onChange={handleEffortsChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />
      );
    },
    [handleEffortsChange, handleInputFocus, handleInputBlur]
  );

  const columns = useMemo(
    () => [
      {
        title: "Action Type",
        dataIndex: "action_type",
        key: "action_type",
        width: 200,
        className: "roi-grid-cell",
        render: (text, record) => {
          if (record.key === "total") {
            return (
              <Text strong className="roi-total-label">
                {text}
              </Text>
            );
          }
          return (
            <div className="roi-action-type-container">
              <Text className="roi-action-type-text">{text}</Text>
              {record.info_text && (
                <Tooltip
                  title={record.info_text}
                  placement="top"
                  className="roi-action-info-tooltip"
                >
                  <i className="fa fa-info-circle roi-action-info-icon" />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: (
          <div className="roi-column-header">
            <Text>No. of times</Text>
            <Text>action taken</Text>
          </div>
        ),
        dataIndex: "times_taken",
        key: "times_taken",
        width: 120,
        align: "center",
        className: "roi-grid-cell roi-grid-center",
        render: (value, record) => (
          <Text
            className={
              record.key === "total" ? "roi-total-value" : "roi-times-value"
            }
            strong={record.key === "total"}
          >
            {value?.toLocaleString() || "0"}
          </Text>
        ),
      },
      {
        title: (
          <div className="roi-column-header">
            <Text>Efforts required</Text>
            <Text>(in mins)</Text>
          </div>
        ),
        dataIndex: "efforts_required",
        key: "efforts_required",
        width: 130,
        align: "center",
        className: "roi-grid-cell roi-grid-center roi-editable-cell",
        render: renderEffortsInput,
      },
      {
        title: (
          <div className="roi-column-header">
            <Text>Total time taken</Text>
          </div>
        ),
        key: "total_time",
        width: 130,
        align: "center",
        className: "roi-grid-cell roi-grid-center roi-grid-calculated",
        render: (_, record) => {
          if (record.key === "total") {
            return (
              <div className="roi-total-highlight">
                <Text strong className="roi-total-time-value">
                  {record.total_time}
                </Text>
                <Text className="roi-total-time-unit">MINS</Text>
              </div>
            );
          }
          return (
            <div className="roi-total-time-cell">
              <Text strong className="roi-calculated-value">
                {record.times_taken * record.efforts_required}
              </Text>
              <Text className="roi-time-unit">min</Text>
            </div>
          );
        },
      },
    ],
    [renderEffortsInput]
  );

  const getRowKey = useCallback((record) => `roi-row-${record.key}`, []);

  // Memoize table props to prevent re-rendering
  const tableProps = useMemo(
    () => ({
      pagination: false,
      size: "small",
      className: "roi-grid-table roi-no-measure-row",
      bordered: true,
      scroll: { x: 580 },
      style: { width: "100%", tableLayout: "fixed" },
      components: {
        table: (props) => (
          <table {...props} style={{ tableLayout: "fixed", width: "100%" }} />
        ),
      },
    }),
    []
  );

  return (
    <div className="roi-efforts-grid">
      {/* Assumptions Note */}
      <div className="roi-assumptions-note">
        <Text className="roi-assumptions-text">* Assumptions</Text>
      </div>

      <div className="roi-grid-container">
        <div className="roi-main-table-wrapper">
          <Table
            key="roi-main-table"
            columns={columns}
            dataSource={gridData}
            rowKey={getRowKey}
            {...tableProps}
          />

          {/* Total Row using Table structure for perfect alignment */}
          <Table
            key="roi-total-table"
            columns={columns}
            dataSource={[
              {
                key: "total",
                action_type: "TOTAL",
                info_text: "",
                times_taken: totalValues.total_times,
                efforts_required: "",
                total_time: totalValues.total_time_taken,
              },
            ]}
            showHeader={false}
            style={{ width: "100%", marginTop: "-1px", tableLayout: "fixed" }}
            rowKey={getRowKey}
            {...tableProps}
            className="roi-total-table roi-no-measure-row"
          />
        </div>
      </div>
    </div>
  );
};

export default RoiEffortsGrid;
